import { betterAuthClient } from '@/lib/auth/better-auth-client'
import { API_BASE_URL } from '@/lib/env'

export type HelmSessionUser = {
  id: string
  email?: string | null
  name?: string | null
}

export async function getHelmSessionUser(): Promise<HelmSessionUser | null> {
  const { data } = await betterAuthClient.getSession()
  const user = data?.user

  if (!user?.id) {
    return null
  }

  return {
    id: user.id,
    email: user.email,
    name: user.name,
  }
}

export function normalizeHelmRedirectPath(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return '/'
  }

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://helm.local'
    const url = new URL(value, origin)
    if (url.origin !== origin) {
      return '/'
    }

    return `${url.pathname}${url.search}${url.hash}` || '/'
  } catch {
    return '/'
  }
}

export function getHelmLoginUrl(nextPath?: string) {
  const next = normalizeHelmRedirectPath(nextPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`)
  return `/login?next=${encodeURIComponent(next)}`
}

function getAuthErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }

  return fallback
}

export async function requestHelmPasswordSession(email: string, password: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/password/session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  })

  if (response.ok) {
    return
  }

  let message = 'Failed to sign in'
  try {
    const body = await response.json() as { error?: { message?: unknown } }
    message = getAuthErrorMessage(body.error, message)
  } catch {
    message = `${message} (${response.status})`
  }

  throw new Error(message)
}
